/*
Species index loader — mirrors Android SpeciesIndex.kt.

The on-disk format (`/data/species-index.json`) is an array produced by
`tools/build_dataset.py`:

    [{ "i": 0, "g": "Aesculus", "e": "hippocastanum", "nc": "Marronnier" }, ...]

- `i`  : stable integer index — referenced by CaptureEntity.speciesIndex and
        by feature.properties.sk in the geojson. Never reorder; preserved
        across rebuilds by the build script.
- `g`  : genus (Latin, capitalized).
- `e`  : species epithet (Latin, lowercase).
- `nc` : nom commun (French common name) — optional. When present, this is
        the canonical user-facing label.

This loader builds two memoized maps in one pass:
- forward:  "<genre>__<espece>" -> int           (used by tools/import paths)
- reverse:  int -> { genre, espece, commonName } (used by the Arboretum)
*/

import { fetchJson } from './fetchJson'

const kAssetPath = '/data/species-index.json'  // canonical location served by the SW

export interface SpeciesName
{
	genre: string
	espece: string
	commonName: string | null
}

interface RawEntry
{
	i: number
	g: string
	e: string
	nc?: string
}

let fForward: Map<string, number> | null = null  // memoized "genre__espece" -> int
let fReverse: Map<number, SpeciesName> | null = null  // memoized int -> name pair

/*
Ensure the species index is loaded. Idempotent — only the first call hits
the network. Builds both forward and reverse maps in a single pass.
Failure leaves both maps null and rethrows so the UI can surface the error.
*/
export async function ensureSpeciesIndex(): Promise<Map<string, number>>
{
	if (fForward) return fForward
	const vRaw = await fetchJson<RawEntry[]>(kAssetPath)  // typed-as array; throws on bad JSON
	const vForward = new Map<string, number>()
	const vReverse = new Map<number, SpeciesName>()
	for (const vEntry of vRaw)
	{
		vForward.set(`${vEntry.g}__${vEntry.e}`, vEntry.i)
		vReverse.set(vEntry.i, {
			genre: vEntry.g,
			espece: vEntry.e,
			commonName: vEntry.nc && vEntry.nc.length > 0 ? vEntry.nc : null,
		})
	}
	fForward = vForward
	fReverse = vReverse
	return fForward
}

/*
Look up the int index for a (genre, espece) pair. Returns null if the
species is not in the index — caller decides whether to treat this as a
soft miss or a hard data error.
*/
export function speciesIndexFor(inGenre: string, inEspece: string): number | null
{
	if (!fForward) return null
	const vKey = `${inGenre}__${inEspece}`  // composite key matching the build script
	return fForward.get(vKey) ?? null
}

/*
Reverse lookup: int index -> {genre, espece, commonName}. Used by the
Arboretum and the capture detail modal to display species names from
capture rows / map features that only carry the int index (`sk`).
*/
export function speciesNameFor(inIndex: number): SpeciesName | null
{
	if (!fReverse) return null
	return fReverse.get(inIndex) ?? null
}

/*
Iterate every loaded species. Used by the search sheet to filter on
common name + Latin binomial. Returns an empty array before the
index is loaded; caller is expected to `await ensureSpeciesIndex()`
first.
*/
export function allSpecies(): { sk: number; name: SpeciesName }[]
{
	if (!fReverse) return []
	const vOut: { sk: number; name: SpeciesName }[] = []
	for (const [vSk, vName] of fReverse) vOut.push({ sk: vSk, name: vName })
	return vOut
}
